import { useEffect, useMemo, useState } from 'react';
import type { Founder, FoundersListResponse } from '../types';
import { listFounders } from '../lib/api';
import { IconSearch, IconRefresh } from '../components/icons';
import { relativeDate, toCsv, downloadCsv } from '../lib/format';
import { SkeletonRows } from '../components/Skeleton';
import { useToast } from '../lib/toast';

const STAGES = ['Pre-Seed', 'Seed', 'Series A', 'Series B', 'Series C', 'Growth'];
const PATHS = ['audit', 'fit', 'cfo'];
const RAISE_TIMINGS = ['active', 'soon', 'planning', 'exploring'];

export type FoundersPreFilter =
  | { kind: 'auditCompleted' }
  | { kind: 'profileCompleted' }
  | { kind: 'profileStarted' }
  | { kind: 'searched' }
  | { kind: 'visit' };

interface FoundersPageProps {
  preFilter?: FoundersPreFilter | null;
  clearPreFilter?: () => void;
}

const PRE_FILTER_LABELS: Record<FoundersPreFilter['kind'], string> = {
  visit:            'All visitors',
  profileStarted:   'Profile started',
  profileCompleted: 'Profile completed (email captured)',
  searched:         'Searched funds',
  auditCompleted:   'Audit completed',
};

export function FoundersPage({ preFilter, clearPreFilter }: FoundersPageProps = {}) {
  const [items, setItems] = useState<Founder[]>([]);
  const [summary, setSummary] = useState<FoundersListResponse['summary'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [pathFilter, setPathFilter] = useState('');
  const [timingFilter, setTimingFilter] = useState('');
  const [selected, setSelected] = useState<Founder | null>(null);
  const toast = useToast();

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const res = await listFounders();
      setItems(res.items);
      setSummary(res.summary);
      if (res.items.length && !selected) setSelected(res.items[0]);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to load founders';
      setError(msg);
      toast.error('Failed to load founders', msg);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter(i => {
      if (q) {
        const hay = [i.firstName, i.lastName, i.email, i.company, i.role].filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (stageFilter && i.stage !== stageFilter) return false;
      if (pathFilter && i.pathPicked !== pathFilter) return false;
      if (timingFilter && i.raiseTiming !== timingFilter) return false;
      // Funnel-stage drill-down filters from Dashboard.
      if (preFilter) {
        if (preFilter.kind === 'profileStarted'   && !i.stage) return false;
        if (preFilter.kind === 'profileCompleted' && !i.email) return false;
        if (preFilter.kind === 'searched'         && !(typeof i.searchCount === 'number' && i.searchCount > 0)) return false;
        if (preFilter.kind === 'auditCompleted'   && !i.auditSubmittedAt) return false;
        // 'visit' = all rows, no extra filter.
      }
      return true;
    });
  }, [items, search, stageFilter, pathFilter, timingFilter, preFilter]);

  function exportCsv() {
    const csv = toCsv(filtered, [
      { key: 'firstName', label: 'First name' },
      { key: 'lastName', label: 'Last name' },
      { key: 'email', label: 'Email' },
      { key: 'company', label: 'Company' },
      { key: 'role', label: 'Role' },
      { key: 'city', label: 'City' },
      { key: 'lang', label: 'Lang' },
      { key: 'stage', label: 'Stage' },
      { key: 'sectors', label: 'Sectors' },
      { key: 'roundSize', label: 'Round size' },
      { key: 'raiseTiming', label: 'Raise timing' },
      { key: 'pathPicked', label: 'Path' },
      { key: 'auditScore', label: 'Audit score' },
      { key: 'auditBand', label: 'Audit band' },
      { key: 'auditSubmittedAt', label: 'Audit at' },
      { key: 'firstSeenAt', label: 'First seen' },
      { key: 'lastActiveAt', label: 'Last active' },
      { key: 'sessionCount', label: 'Sessions' },
      { key: 'searchCount', label: 'Searches' },
      { key: 'emailOptIn', label: 'Email opt-in' },
    ]);
    downloadCsv(`founders-${new Date().toISOString().slice(0, 10)}.csv`, csv);
    toast.success(`Exported ${filtered.length} founders`);
  }

  return (
    <section className="px-8 py-7 max-w-[1400px]">
      <div className="flex items-end justify-between mb-5">
        <div>
          <h1 className="h-display text-[26px]">Founders</h1>
          <p className="text-[13.5px] text-ink-muted mt-0.5">
            {summary ? `${summary.total} profiles captured via the public site.` : 'Loading…'} Read-only with internal annotation in V2.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={refresh} className="h-9 px-3 text-[13px] rounded-md border border-line bg-white hover:bg-cream-tint flex items-center gap-1.5">
            <IconRefresh className={'w-4 h-4 text-ink-muted ' + (loading ? 'animate-spin' : '')} />
            Refresh
          </button>
          <button onClick={exportCsv} disabled={!filtered.length} className="h-9 px-3 text-[13px] rounded-md border border-line bg-white hover:bg-cream-tint flex items-center gap-1.5 disabled:opacity-50">
            Export CSV ({filtered.length})
          </button>
        </div>
      </div>

      {/* Mini-stats strip */}
      <div className="grid grid-cols-5 gap-4 mb-5">
        <Stat label="Total profiles" value={summary?.total ?? 0} hint={loading ? '…' : `${items.length} loaded`} />
        <Stat label="Completed" value={summary?.completed ?? 0} hint={summary && summary.total ? `${pct(summary.completed, summary.total)}` : ''} />
        <Stat label="Audit completed" value={summary?.auditCompleted ?? 0} hint={summary && summary.total ? `${pct(summary.auditCompleted, summary.total)}` : ''} />
        <Stat label="Email opt-in" value={summary?.emailOptIn ?? 0} hint={summary && summary.total ? `${pct(summary.emailOptIn, summary.total)}` : ''} />
        <Stat label="Active last 7d" value={summary?.activeLast7d ?? 0} hint={summary && summary.total ? `${pct(summary.activeLast7d, summary.total)}` : ''} />
      </div>

      {error && <div className="mb-4 px-3 py-2 rounded-md bg-red-50 border border-red-200 text-red-800 text-[13px]">{error}</div>}

      {preFilter && (
        <div className="mb-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-founders-soft text-founders-deep text-[12.5px] font-medium">
          <span>Drilled down from Dashboard funnel: {PRE_FILTER_LABELS[preFilter.kind]}</span>
          <button onClick={clearPreFilter} className="hover:bg-founders/20 rounded-full w-4 h-4 grid place-items-center" title="Clear">
            <svg className="w-3 h-3" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={2}><path d="M5 5l10 10M15 5L5 15" /></svg>
          </button>
        </div>
      )}

      <div className="grid grid-cols-[1fr_380px] gap-4">
        {/* Table */}
        <div className="bg-white border border-line rounded-lg shadow-card">
          <div className="px-4 py-3 flex items-center gap-2 flex-wrap border-b border-line-soft">
            <div className="relative">
              <IconSearch className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-faint" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Filter by name, email, company..."
                className="h-8 w-[280px] pl-8 pr-2.5 text-[13px] rounded-md border border-line bg-cream-tint focus:bg-white ring-input"
              />
            </div>
            <Select value={stageFilter} setValue={setStageFilter} label="Stage" options={STAGES} />
            <Select value={pathFilter} setValue={setPathFilter} label="Path" options={PATHS} />
            <Select value={timingFilter} setValue={setTimingFilter} label="Raise timing" options={RAISE_TIMINGS} />
            <span className="ml-auto text-[12px] text-ink-muted num">{filtered.length} of {items.length}</span>
          </div>

          <div className="overflow-x-auto scroll-area max-h-[calc(100vh-340px)]">
            <table className="w-full text-[13px]">
              <thead className="bg-cream-tint text-[11.5px] uppercase tracking-wider text-ink-muted sticky top-0">
                <tr>
                  <Th>Founder</Th>
                  <Th>Stage</Th>
                  <Th>Sectors</Th>
                  <Th>Round</Th>
                  <Th>Path</Th>
                  <Th>Last active</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line-soft">
                {loading && items.length === 0 && (
                  <SkeletonRows rows={8} cols={6} />
                )}
                {!loading && filtered.length === 0 && (
                  <tr><td colSpan={6} className="text-center py-12 text-ink-muted">No founders match the current filters.</td></tr>
                )}
                {filtered.map(f => {
                  const active = selected?.id === f.id;
                  const name = [f.firstName, f.lastName].filter(Boolean).join(' ') || '(no name)';
                  return (
                    <tr
                      key={f.id}
                      onClick={() => setSelected(f)}
                      className={'cursor-pointer transition ' + (active ? 'bg-founders-soft/40 hover:bg-founders-soft/60' : 'hover:bg-cream-tint')}
                    >
                      <td className="px-4 py-3">
                        <div className="font-medium text-ink leading-tight">{name}</div>
                        <div className="text-[11.5px] text-ink-muted leading-tight truncate max-w-[280px]">{f.email ?? '-'}{f.company ? ` · ${f.company}` : ''}</div>
                      </td>
                      <td className="px-3 py-3">{f.stage ?? '-'}</td>
                      <td className="px-3 py-3">
                        <div className="flex flex-wrap gap-1">
                          {(f.sectors ?? []).slice(0, 2).map(s => (
                            <span key={s} className="inline-flex items-center px-1.5 py-px rounded-full text-[11px] font-medium bg-cream-deep text-navy">{s}</span>
                          ))}
                        </div>
                      </td>
                      <td className="px-3 py-3 num">{f.roundSize ?? '-'}</td>
                      <td className="px-3 py-3">
                        {f.pathPicked ? (
                          <span className={'inline-flex items-center px-1.5 py-px rounded-full text-[11px] font-medium ' + (
                            f.pathPicked === 'audit' ? 'bg-amber-soft text-amber'
                            : f.pathPicked === 'fit' ? 'bg-founders-soft text-founders-deep'
                            : 'bg-cream-deep text-navy'
                          )}>{f.pathPicked}</span>
                        ) : '-'}
                      </td>
                      <td className="px-3 py-3 text-ink-muted">{relativeDate(f.lastActiveAt ?? f.firstSeenAt)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Detail panel */}
        <FounderDetail founder={selected} />
      </div>
    </section>
  );
}

/* ---------- Detail panel ---------- */

function FounderDetail({ founder }: { founder: Founder | null }) {
  if (!founder) {
    return (
      <aside className="bg-white border border-line rounded-lg shadow-card p-6 sticky top-[88px] self-start text-[13.5px] text-ink-muted">
        Select a founder to see details.
      </aside>
    );
  }
  const name = [founder.firstName, founder.lastName].filter(Boolean).join(' ') || '(no name)';
  const initials = (founder.firstName?.[0] || '').toUpperCase() + (founder.lastName?.[0] || '').toUpperCase();

  return (
    <aside className="bg-white border border-line rounded-lg shadow-card p-5 sticky top-[88px] self-start max-h-[calc(100vh-110px)] overflow-y-auto scroll-area">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-full bg-founders-soft text-founders-deep grid place-items-center font-display text-[14px]">{initials || '?'}</div>
          <div className="min-w-0">
            <div className="h-display text-[15px] leading-tight truncate">{name}</div>
            <div className="text-[12px] text-ink-muted leading-tight truncate">{[founder.role, founder.company].filter(Boolean).join(' · ')}</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 text-[12.5px] mb-4">
        <KV label="Email" value={founder.email} />
        <KV label="City" value={founder.city} />
        <KV label="Lang" value={founder.lang} />
        <KV label="Stage" value={founder.stage} />
        <KV label="Round size" value={founder.roundSize} />
        <KV label="Raise role" value={founder.raiseRole} />
        <KV label="Conviction" value={founder.convictionPref} />
        <KV label="Follow-on need" value={founder.followOnNeed} />
        <KV label="Path picked" value={founder.pathPicked} />
        <KV label="Email opt-in" value={founder.emailOptIn === true ? 'yes' : founder.emailOptIn === false ? 'no' : ''} />
      </div>

      {(founder.sectors?.length ?? 0) > 0 && (
        <div className="mb-4">
          <div className="text-[11px] uppercase tracking-wider text-ink-muted mb-1.5">Sectors</div>
          <div className="flex flex-wrap gap-1">
            {(founder.sectors ?? []).map(s => (
              <span key={s} className="inline-flex items-center px-2 py-px rounded-full text-[11.5px] font-medium bg-cream-deep text-navy">{s}</span>
            ))}
          </div>
        </div>
      )}

      {founder.auditSubmittedAt && (
        <div className="mb-4">
          <div className="text-[11px] uppercase tracking-wider text-ink-muted mb-1.5">Audit</div>
          <div className="bg-amber-soft border border-amber/30 rounded-md p-3 text-[12.5px]">
            <div className="flex items-center justify-between mb-1">
              <span className="font-medium text-ink">Score: <span className="num">{founder.auditScore ?? '-'}</span> {founder.auditBand && <span className="text-ink-muted">({founder.auditBand})</span>}</span>
              <span className="text-ink-muted">{relativeDate(founder.auditSubmittedAt)}</span>
            </div>
          </div>
        </div>
      )}

      <div className="mb-4">
        <div className="text-[11px] uppercase tracking-wider text-ink-muted mb-2">Activity timeline</div>
        <ol className="relative pl-4 border-l border-line space-y-3 text-[12.5px]">
          {timelineEvents(founder).map((ev, i) => (
            <li key={i}>
              <span className={'absolute -left-[5px] mt-1.5 w-2 h-2 rounded-full ' + ev.dot} />
              <div className="leading-snug">{ev.label}</div>
              <div className="text-ink-faint num">{relativeDate(ev.at)}</div>
            </li>
          ))}
        </ol>
      </div>

      <div className="grid grid-cols-2 gap-3 text-[12.5px] pt-3 border-t border-line-soft">
        <KV label="First seen" value={relativeDate(founder.firstSeenAt)} />
        <KV label="Last active" value={relativeDate(founder.lastActiveAt)} />
        <KV label="Sessions" value={founder.sessionCount?.toString()} />
        <KV label="Searches" value={founder.searchCount?.toString()} />
        <KV label="Compared" value={founder.comparedCount?.toString()} />
        <KV label="Shortlist" value={founder.shortlistCount?.toString()} />
        <KV label="Referrer" value={founder.referrer} />
        <KV label="UTM source" value={founder.utmSource} />
      </div>
    </aside>
  );
}

function timelineEvents(f: Founder): { dot: string; label: string; at?: string }[] {
  const events: { dot: string; label: string; at?: string }[] = [];
  if (f.auditSubmittedAt) events.push({ dot: 'bg-amber', label: `Audit completed${typeof f.auditScore === 'number' ? ` · score ${f.auditScore}` : ''}`, at: f.auditSubmittedAt });
  if (f.contactSubmittedAt) events.push({ dot: 'bg-navy', label: `Contact form: ${f.contactTopic ?? 'message'}`, at: f.contactSubmittedAt });
  if (f.auditStartedAt) events.push({ dot: 'bg-cream-deep', label: 'Audit started', at: f.auditStartedAt });
  if (f.searchCount && f.searchCount > 0) events.push({ dot: 'bg-funds', label: `Searched funds (${f.searchCount}×)`, at: f.lastActiveAt });
  if (f.email) events.push({ dot: 'bg-founders', label: 'Profile completed', at: f.firstSeenAt });
  if (f.firstSeenAt) events.push({ dot: 'bg-line', label: `First seen${f.referrer ? ` · ${f.referrer.replace(/^https?:\/\/(www\.)?/, '').slice(0, 40)}` : ''}`, at: f.firstSeenAt });
  return events.sort((a, b) => Date.parse(b.at ?? '') - Date.parse(a.at ?? ''));
}

/* ---------- helpers ---------- */

function pct(num: number, denom: number): string {
  if (!denom) return '-';
  return `${((num / denom) * 100).toFixed(1)}%`;
}

function Th({ children }: { children?: React.ReactNode }) {
  return <th className="text-left font-medium px-3 py-2.5">{children}</th>;
}

function KV({ label, value }: { label: string; value?: string }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-ink-muted">{label}</div>
      <div className="font-medium truncate" title={value}>{value || '-'}</div>
    </div>
  );
}

function Stat({ label, value, hint }: { label: string; value: number; hint?: string }) {
  return (
    <div className="bg-white border border-line rounded-lg p-4">
      <div className="text-[11.5px] uppercase tracking-wider text-ink-muted">{label}</div>
      <div className="num h-display text-[20px] mt-1">{value}</div>
      {hint && <div className="text-[11.5px] text-ink-muted mt-1">{hint}</div>}
    </div>
  );
}

function Select({ value, setValue, label, options }: { value: string; setValue: (v: string) => void; label: string; options: string[] }) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={e => setValue(e.target.value)}
        className="h-8 pl-2.5 pr-7 text-[12.5px] rounded-md border border-line bg-white hover:bg-cream-tint appearance-none cursor-pointer"
      >
        <option value="">{label}: all</option>
        {options.map(o => <option key={o} value={o}>{label}: {o}</option>)}
      </select>
      <svg className="w-3 h-3 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-ink-muted" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.5}><path d="M6 8l4 4 4-4" /></svg>
    </div>
  );
}

